import {DatePipe, isPlatformBrowser, NgForOf, NgIf} from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FaqSectionComponent} from '../../ui/faq-section/faq-section.component';
import {
    PublicCostPerKmCalculatorComponent,
} from '../../ui/public-cost-per-km-calculator/public-cost-per-km-calculator.component';
import {BlogPost} from '../../../interfaces/blog';
import {AosService} from '../../../services/aos.service';
import {BlogService} from '../../../services/blog.service';
import {SeoService} from '../../../services/seo.service';
import {env} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';

@Component({
    selector: 'app-blog-detail-page',
    standalone: true,
    imports: [
        DatePipe,
        NgForOf,
        NgIf,
        FaqSectionComponent,
        PublicCostPerKmCalculatorComponent,
        RouterLink,
    ],
    templateUrl: './blog-detail-page.component.html',
    styleUrl: './blog-detail-page.component.scss',
})
export class BlogDetailPageComponent implements OnInit, AfterViewInit, OnDestroy {
    post?: BlogPost;
    contentHtml = '';
    isLoading = true;
    hasLoadError = false;
    hasSeenCostCalculator = false;
    isCostCalculatorVisible = false;
    readonly contentSkeletonRows = Array.from({length: 8});
    protected readonly env = env;
    private costCalculatorObserver?: IntersectionObserver;

    @ViewChild('costCalculatorContainer')
    set costCalculatorContainer(element: ElementRef<HTMLElement> | undefined) {
        this.costCalculatorObserver?.disconnect();

        if (!element || !isPlatformBrowser(this.platformId)) {
            return;
        }

        this.costCalculatorObserver = new IntersectionObserver(([entry]) => {
            this.isCostCalculatorVisible = entry.isIntersecting;
            this.hasSeenCostCalculator ||= entry.isIntersecting;
            this.changeDetectorRef.markForCheck();
        }, {threshold: 0.05});
        this.costCalculatorObserver.observe(element.nativeElement);
    }

    constructor(
        private route: ActivatedRoute,
        private aosService: AosService,
        private blogService: BlogService,
        private seoService: SeoService,
        private analytics: AnalyticsService,
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        const slug = this.route.snapshot.paramMap.get('slug') || '';
        this.blogService.getPost(slug).subscribe({
            next: (post) => {
                this.post = post;
                this.contentHtml = this.normalizeContentImageUrls(post);
                this.isLoading = false;

                this.seoService.applyPage({
                    title: post.title,
                    h1: post.h1,
                    description: post.description,
                    og_title: post.og_title || post.title,
                    og_description: post.og_description || post.description,
                    keywords: post.keywords || '',
                }, {
                    path: `/blog/${post.slug}`,
                    type: 'article',
                    image: post.og_image?.url || post.hero_image?.url,
                    imageAlt: post.og_image?.alt || post.hero_image?.alt || post.h1,
                    robots: post.indexed === false ? 'noindex,nofollow' : undefined,
                    faq: post.faq,
                    breadcrumb: [
                        {name: 'Главная', path: '/'},
                        {name: 'Блог', path: '/blog'},
                        {name: post.breadcrumb || post.h1, path: `/blog/${post.slug}`},
                    ],
                });
                void this.aosService.refresh();
            },
            error: () => {
                this.hasLoadError = true;
                this.isLoading = false;
                void this.aosService.refresh();
            },
        });
    }

    ngAfterViewInit(): void {
        void this.aosService.refresh();
    }

    ngOnDestroy(): void {
        this.costCalculatorObserver?.disconnect();
    }

    getAosDelay(index: number, step = 90, max = 270): string {
        return `${Math.min(index * step, max)}`;
    }

    shouldShowCostPerKmCalculator(): boolean {
        return this.post?.slug === 'stoimost-1-km-probega-avtomobilya';
    }

    registrationUrl(source: string): string {
        return this.analytics.registrationUrl(source);
    }

    trackRegistrationClick(source: string): void {
        this.analytics.goal('registration_click', {
            source,
            article: this.post?.slug || '',
        });
    }

    trackCostCalculatorRegistrationClick(source: string): void {
        this.analytics.goal('cost_km_registration_click', {
            source,
            article: this.post?.slug || '',
        });
        this.trackRegistrationClick(source);
    }

    private normalizeContentImageUrls(post: BlogPost): string {
        const imageOrigin = this.getImageOrigin(post);
        if (!imageOrigin || !post.content) {
            return post.content || '';
        }

        return post.content
            .replace(/\bsrc=(["'])\/img\//g, `src=$1${imageOrigin}/img/`)
            .replace(/\bsrcset=(["'])\/img\//g, `srcset=$1${imageOrigin}/img/`);
    }

    private getImageOrigin(post: BlogPost): string | null {
        const imageUrl = post.hero_image?.url || post.og_image?.url || post.gallery[0]?.url || post.gallery[0]?.thumb_url;
        if (!imageUrl) {
            return null;
        }

        try {
            return new URL(imageUrl).origin;
        } catch {
            return null;
        }
    }
}
